import React, { useState, useEffect } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { justificatifService, clientService } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { Search, Download, CheckCircle, XCircle, Clock, Eye } from 'lucide-react';

const JUSTIFICATIF_LABELS = {
  'piece_identite': 'Pièce d\'identité',
  'rib': 'RIB',
  'justificatif_domicile': 'Justificatif de domicile',
  'avis_imposition': 'Avis d\'imposition',
  'releve_compte': 'Relevé de compte'
};

const AdminJustificatifs = () => {
  const { toast } = useToast();
  const [justificatifs, setJustificatifs] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [justifData, clientData] = await Promise.all([
        justificatifService.getAll(),
        clientService.getAll()
      ]);
      setJustificatifs(justifData);
      setClients(clientData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les données',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? `${client.firstName} ${client.lastName}` : 'Inconnu';
  };

  const handleStatusChange = async (justificatifId, newStatus) => {
    try {
      await justificatifService.update(justificatifId, { status: newStatus });
      
      setJustificatifs(justificatifs.map(j => 
        j.id === justificatifId ? { ...j, status: newStatus } : j
      ));

      toast({
        title: 'Statut mis à jour',
        description: `Le document a été ${newStatus.toLowerCase()}`,
      });
    } catch (error) {
      console.error('Status update error:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de mettre à jour le statut',
        variant: 'destructive',
      });
    }
  };

  const handleDownload = async (justificatifId, fileName) => {
    try {
      const blob = await justificatifService.download(justificatifId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de télécharger le fichier',
        variant: 'destructive',
      });
    }
  };

  const filteredJustificatifs = justificatifs.filter(justif => {
    const clientName = getClientName(justif.clientId).toLowerCase();
    const matchesSearch = clientName.includes(searchTerm.toLowerCase()) ||
                         justif.fileName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || justif.status === filterStatus;
    const matchesType = filterType === 'all' || justif.type === filterType;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Validé':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'En attente':
        return <Clock className="h-5 w-5 text-orange-500" />;
      case 'Refusé':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Validé': 'bg-green-100 text-green-700',
      'En attente': 'bg-orange-100 text-orange-700',
      'Refusé': 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  // Group justificatifs by client
  const justificatifsByClient = {};
  clients.forEach(client => {
    justificatifsByClient[client.id] = {
      client,
      justificatifs: justificatifs.filter(j => j.clientId === client.id),
      completion: 0
    };
    
    const validated = justificatifsByClient[client.id].justificatifs.filter(j => j.status === 'Validé').length;
    justificatifsByClient[client.id].completion = Math.round((validated / 5) * 100);
  });

  return (
    <div className="flex">
      <AdminSidebar />
      <div className="flex-1 bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 p-6">
          <h1 className="text-2xl font-bold text-gray-900">Gestion des justificatifs</h1>
          <p className="text-gray-600 mt-1">
            Consultez et validez les documents des clients
          </p>
        </div>

        {/* Main Content */}
        <div className="p-6">
          {/* Stats Cards */}
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total documents</p>
                  <p className="text-2xl font-bold text-gray-900">{justificatifs.length}</p>
                </div>
                <div className="p-3 rounded-lg bg-blue-50">
                  <Eye className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </Card>
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">En attente</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {justificatifs.filter(j => j.status === 'En attente').length}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-orange-50">
                  <Clock className="h-6 w-6 text-orange-600" />
                </div>
              </div>
            </Card>
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Validés</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {justificatifs.filter(j => j.status === 'Validé').length}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-green-50">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </Card>
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Refusés</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {justificatifs.filter(j => j.status === 'Refusé').length}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-red-50">
                  <XCircle className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </Card>
          </div>

          {/* Filters */}
          <Card className="p-4 mb-6">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Rechercher par client ou fichier..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Filtrer par statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="En attente">En attente</SelectItem>
                  <SelectItem value="Validé">Validé</SelectItem>
                  <SelectItem value="Refusé">Refusé</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger>
                  <SelectValue placeholder="Filtrer par type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  {Object.entries(JUSTIFICATIF_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </Card>

          {/* Justificatifs Table */}
          <Card>
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Client</TableHead>
                  <TableHead>Type de document</TableHead>
                  <TableHead>Fichier</TableHead>
                  <TableHead>Date d'upload</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredJustificatifs.map((justif) => (
                  <TableRow key={justif.id} className="hover:bg-gray-50">
                    <TableCell>
                      <div className="font-medium text-gray-900">
                        {getClientName(justif.clientId)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-gray-700">
                        {JUSTIFICATIF_LABELS[justif.type]}
                      </span>
                    </TableCell>
                    <TableCell className="text-gray-600">{justif.fileName}</TableCell>
                    <TableCell className="text-gray-600">
                      {new Date(justif.uploadedAt).toLocaleDateString('fr-FR')}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusBadge(justif.status)}`}>
                          {justif.status}
                        </span>
                        {getStatusIcon(justif.status)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDownload(justif.id, justif.fileName)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        {justif.status !== 'Validé' && (
                          <Button
                            size="sm"
                            className="text-white"
                            style={{ backgroundColor: '#10B981' }}
                            onClick={() => handleStatusChange(justif.id, 'Validé')}
                          >
                            Valider
                          </Button>
                        )}
                        {justif.status !== 'Refusé' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            onClick={() => handleStatusChange(justif.id, 'Refusé')}
                          >
                            Refuser
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminJustificatifs;
